/* eslint-disable jsx-a11y/no-static-element-interactions */
/* eslint-disable jsx-a11y/click-events-have-key-events */
/* eslint-disable jsx-a11y/interactive-supports-focus */
import RenderMarkdown from '@src/components/RTEditor/RenderMarkdown';
import { MediaType } from '@src/model/frontendmodel';
import { QuotedMessage } from '@src/types';
import { ChatContact } from '@src/types/chats';
import { useMemo } from 'react';
import { useIntl } from 'react-intl';
import { useChatHistoryContext } from '../ChatHistoryContext';
import ChatMessageAudioQuoted from '../ChatMessageContent/ChatMessageAudioQuoted';
import ChatMessageFileQuoted from '../ChatMessageContent/ChatMessageFileQuoted';
import ChatMessageImageQuoted from '../ChatMessageContent/ChatMessageImageQuoted';
import ChatMessageLocationQuoted from '../ChatMessageContent/ChatMessageLocationQuoted';
import ChatMessageSticker from '../ChatMessageContent/ChatMessageSticker';
import ChatMessageVideoQuoted from '../ChatMessageContent/ChatMessageVideoQuoted';
import ChatMessageContactQuoted from './ChatMessageContact/ChatMessageContactQuoted';

type ChatMessageQuotedProps = {
  contact: ChatContact;
  quotedMessage: QuotedMessage;
  noShowAuthor?: boolean;
};

const ChatMessageQuoted: React.FC<ChatMessageQuotedProps> = ({
  contact,
  quotedMessage,
  noShowAuthor = false,
}) => {
  const { formatMessage } = useIntl();

  const { focusMessage } = useChatHistoryContext();

  const isIncomingQuotedMsg = useMemo(
    () => quotedMessage?.direction === 'INCOMING',
    [quotedMessage]
  );

  const isOutgoingQuotedMsg = useMemo(
    () => quotedMessage?.direction === 'OUTGOING',
    [quotedMessage]
  );

  const contactName = useMemo(
    () => (contact?.fullName ? contact.fullName : quotedMessage?.from),
    [contact, quotedMessage]
  );

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const title = !noShowAuthor ? (
    <div className="ChatMessageTitle">
      {isIncomingQuotedMsg && contactName}
      {isOutgoingQuotedMsg &&
        formatMessage({ id: 'AgentChatTimeline.myMessage' })}
    </div>
  ) : null;

  const messageContentQuoted = useMemo(() => {
    switch (quotedMessage?.type || quotedMessage?.mediaType) {
      case MediaType.IMAGE:
        return <ChatMessageImageQuoted title={title} message={quotedMessage} />;

      case MediaType.AUDIO:
        return <ChatMessageAudioQuoted title={title} message={quotedMessage} />;

      case MediaType.VIDEO:
        return <ChatMessageVideoQuoted title={title} message={quotedMessage} />;

      case MediaType.STICKER:
        return (
          <>
            {title}
            <ChatMessageSticker
              mediaUrl={quotedMessage.mediaUrl || ''}
              isQuoted
            />
          </>
        );

      case MediaType.FILE:
        return <ChatMessageFileQuoted title={title} message={quotedMessage} />;

      case MediaType.LOCATION:
        return (
          <ChatMessageLocationQuoted title={title} message={quotedMessage} />
        );

      case MediaType.PRODUCT_OVERVIEW:
        return (
          <ChatMessageImageQuoted
            title={title}
            message={{ ...quotedMessage, mediaUrl: quotedMessage.imageUrl }}
          />
        );

      case MediaType.CONTACTS:
        return <ChatMessageContactQuoted contacts={quotedMessage?.contacts} />;

      default:
        return (
          <>
            {title}
            <RenderMarkdown markdown={quotedMessage?.text} />
          </>
        );
    }
  }, [quotedMessage, title]);

  const isInteractiveListIncoming =
    quotedMessage.mediaType === MediaType.INTERACTIVE_LIST &&
    isIncomingQuotedMsg;

  return (
    <div
      className={'ChatMessageQuoted'
        .concat(isIncomingQuotedMsg ? ' is-incoming' : '')
        .concat(isOutgoingQuotedMsg ? ' is-outgoing' : '')
        .concat(isInteractiveListIncoming ? ' interactiveList' : '')}
      style={{
        width: '100%',
        cursor: 'pointer',
      }}
      onClick={() => focusMessage(quotedMessage.messageId)}
    >
      {messageContentQuoted}
    </div>
  );
};

export default ChatMessageQuoted;
